import { useState, useEffect } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  LayoutDashboard,
  FileText,
  Users,
  Receipt,
  Calculator,
  Sparkles,
  Settings,
  LogOut,
  Percent,
  Wallet,
  Menu,
  X,
  Upload,
} from "lucide-react";

const NAV = [
  { to: "/app/dashboard", label: "Dashboard", icon: LayoutDashboard, testId: "nav-dashboard" },
  { to: "/app/invoices", label: "Invoices", icon: FileText, testId: "nav-invoices" },
  { to: "/app/income", label: "Income", icon: Wallet, testId: "nav-income" },
  { to: "/app/clients", label: "Clients", icon: Users, testId: "nav-clients" },
  { to: "/app/expenses", label: "Expenses", icon: Receipt, testId: "nav-expenses" },
  { to: "/app/gst", label: "GST", icon: Percent, testId: "nav-gst" },
  { to: "/app/tax", label: "Income Tax", icon: Calculator, testId: "nav-tax" },
  { to: "/app/import", label: "Import", icon: Upload, testId: "nav-import" },
  { to: "/app/ai", label: "AI Co-pilot", icon: Sparkles, testId: "nav-ai" },
  { to: "/app/settings", label: "Settings", icon: Settings, testId: "nav-settings" },
];

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [navOpen, setNavOpen] = useState(false);

  // Close mobile nav on route change
  useEffect(() => { setNavOpen(false); }, [location.pathname]);

  // Lock body scroll while mobile nav is open
  useEffect(() => {
    if (navOpen) {
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = ""; };
    }
  }, [navOpen]);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const sidebarContent = (
    <>
      <div className="px-6 py-6 border-b border-[#e5e5e0] flex items-center justify-between">
        <Link to="/app/dashboard" className="flex items-center gap-2" data-testid="brand-link">
          <span className="block w-2 h-2 bg-[#d95d39]" />
          <div>
            <span className="font-['Outfit'] text-xl font-bold tracking-tight text-[#0a0a0a] block leading-none">
              FinCanvas
            </span>
            <p className="mt-1 text-[10px] tracking-[0.2em] uppercase font-bold text-[#5a5a53]">
              for freelancers
            </p>
          </div>
        </Link>
        <button
          className="md:hidden w-9 h-9 rounded-md hover:bg-[#f4f1eb] flex items-center justify-center text-[#5a5a53]"
          onClick={() => setNavOpen(false)}
          aria-label="Close navigation"
          data-testid="mobile-nav-close"
        >
          <X size={18} />
        </button>
      </div>

      <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
        {NAV.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            data-testid={n.testId}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-3 md:py-2.5 text-sm rounded-md transition-colors ${
                isActive
                  ? "bg-[#0a0a0a] text-[#fafaf8]"
                  : "text-[#5a5a53] hover:bg-[#f4f1eb] hover:text-[#0a0a0a] active:bg-[#f4f1eb]"
              }`
            }
          >
            <n.icon size={16} strokeWidth={1.75} />
            <span>{n.label}</span>
          </NavLink>
        ))}
      </nav>

      <div className="border-t border-[#e5e5e0] p-3">
        <div className="flex items-center gap-3 px-2 py-2">
          <div className="w-8 h-8 rounded-full bg-[#0a0a0a] text-[#fafaf8] flex items-center justify-center text-xs font-bold">
            {(user?.name || "U").slice(0, 1).toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-[#0a0a0a] truncate" data-testid="user-name">
              {user?.name}
            </div>
            <div className="text-[11px] text-[#5a5a53] truncate">{user?.email}</div>
          </div>
        </div>
        <button
          data-testid="logout-btn"
          onClick={handleLogout}
          className="mt-2 w-full flex items-center justify-center gap-2 px-3 py-2 text-xs font-medium text-[#5a5a53] hover:text-[#0a0a0a] border border-[#e5e5e0] rounded-md hover:bg-[#f4f1eb] transition"
        >
          <LogOut size={14} /> Sign out
        </button>
      </div>
    </>
  );

  // Current nav label for mobile top bar
  const current = NAV.find((n) => location.pathname.startsWith(n.to));
  const currentLabel = current?.label || "FinCanvas";

  return (
    <div className="min-h-screen bg-[#fafaf8] flex">
      {/* Desktop sidebar (always visible ≥ md) */}
      <aside
        className="hidden md:flex w-64 shrink-0 border-r border-[#e5e5e0] bg-white flex-col"
        data-testid="app-sidebar"
      >
        {sidebarContent}
      </aside>

      {/* Mobile drawer sidebar */}
      {navOpen && (
        <div className="md:hidden fixed inset-0 z-50 flex" data-testid="mobile-nav-drawer">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setNavOpen(false)}
            aria-hidden="true"
          />
          {/* Panel */}
          <aside className="relative w-[80%] max-w-xs bg-white flex flex-col border-r border-[#e5e5e0] animate-slide-in">
            {sidebarContent}
          </aside>
        </div>
      )}

      {/* Main column */}
      <main className="flex-1 min-w-0 flex flex-col">
        {/* Mobile top bar */}
        <div className="md:hidden sticky top-0 z-30 bg-white/95 backdrop-blur border-b border-[#e5e5e0] flex items-center justify-between px-4 py-3">
          <button
            onClick={() => setNavOpen(true)}
            className="w-10 h-10 rounded-md hover:bg-[#f4f1eb] flex items-center justify-center text-[#0a0a0a]"
            aria-label="Open navigation"
            data-testid="mobile-nav-open"
          >
            <Menu size={20} />
          </button>
          <div className="flex items-center gap-2">
            <span className="block w-1.5 h-1.5 bg-[#d95d39]" />
            <span className="font-['Outfit'] text-base font-bold tracking-tight text-[#0a0a0a]">
              {currentLabel}
            </span>
          </div>
          <div className="w-10 h-10" /> {/* spacer for symmetry */}
        </div>

        <div className="flex-1 overflow-x-hidden">
          <div className="px-4 sm:px-6 md:px-8 py-6 md:py-8 max-w-[1500px]">{children}</div>
        </div>
      </main>
    </div>
  );
}
